import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import f from 'odata-filter-builder';
import buildQuery from 'odata-query';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApplicantDependentModel } from 'services/models/hiring/applicantDependent.model';
import { Filter } from 'services/models/odata/filter';
import { ServiceBase } from 'services/servicebase';
import { HttpService } from '../http.service';
import { TimeShiftHistoryModel } from '../models/corporate/timeShiftHistory.model';
import { EmployeeModel } from '../models/employee.model';
import { EmployeeDismissalTypeModel } from '../models/employee-dismissal-type.model';
import { FileModel } from '../models/file.model';
import { QueryParams } from '../models/odata/queryParams';
import { PermissionModel } from '../models/permission.model';
import { Response } from '../models/response';
import { BenefitDependentModel } from 'services/models/corporate/benefit/benefitSelect.model';
import { KinshipDegreeTypeEnum } from 'services/enums/kinshipDegreeType.enum';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class EmployeeService extends ServiceBase<EmployeeModel> {
  constructor(http: HttpService) {
    super('employee', http, environment.apiUrl);
  }

  getListDismissalTypes(companyId: string): Observable<EmployeeDismissalTypeModel[]> {
    return this.http
      .get(`${this.endpoint}/list-dismissal-types`, { params: { companyId } }, null, null, true)
      .pipe(map((res: any) => (res.body ?? []).map((item) => EmployeeDismissalTypeModel.create(item))));
  }

  createEntity(input: any): EmployeeModel {
    return EmployeeModel.create(input);
  }

  loadEmployeesByCompany(companyId: string): Observable<EmployeeModel[]> {
    return new Observable<any>(observer => {
      (this.list({
        select: ['name', 'cpf', 'companyId', 'enable', 'registration', 'workRegime', 'type'],
        orderBy: 'name',
        orderByOrder: 'asc',
        filter: Filter.equalToGuid('companyId', companyId),
      } as QueryParams).subscribe(res => {
        observer.next(res.data);
      }),
        err => {
          observer.error(err);
        });
    });
  }

  loadEmployeesByNameAndCompany(
    companyIds: string[],
    searchTerm?: string
  ): Observable<EmployeeModel[]> {
    if (!companyIds?.length) {
      return of([]);
    }

    let filter = Filter.build('', Filter.inGuid('companyId', companyIds));

    if (searchTerm?.trim()) {
      const nameFilter = this._buildNameOrRegistrationODataFilter(searchTerm.trim());
      filter = Filter.build(filter, nameFilter);
    }

    return new Observable<EmployeeModel[]>(observer => {
      this.list(
        {
          select: [
            'id',
            'name',
            'profileImagePath',
            'email',
            'companyName',
            'companyId',
            'jobTitleId',
            'jobTitleName',
            'registration',
            'type',
            'shoeSize',
            'shirtSize',
            'pantsSize',
            'enable',
          ],
          orderBy: 'name',
          orderByOrder: 'asc',
          filter,
        } as QueryParams,
        true
      ).subscribe({
        next: res => observer.next(res.data.filter(employee => employee.enable)),
        error: err => observer.error(err),
      });
    });
  }

  loadEmployeesByNameAndOptionalCompany(
    search: string,
    companyId: string = null
  ): Observable<EmployeeModel[]> {
    const nameOrRegistrationFilter = f()
      .contains('name', search)
      .or(f().contains('registration', search))
      .toString();
    const filter = companyId
      ? `${f().eq('companyId', companyId).toString().replace(/'/g, '')} and (${nameOrRegistrationFilter})`
      : nameOrRegistrationFilter;

    return new Observable<any>(observer => {
      (this.list({
        select: ['name', 'cpf', 'companyId', 'registration', 'enable', 'workRegime', 'type'],
        orderBy: 'name',
        orderByOrder: 'asc',
        filter: buildQuery({ filter }).replace('?', '&'),
      } as QueryParams).subscribe(res => {
        observer.next(res.data);
      }),
        err => {
          observer.error(err);
        });
    });
  }

  loadEmployeesByNameAndOthers(
    name: string,
    companyId: string = null,
    costCenterId: any = null,
    timeShiftId: any = null,
    jobtitleId: any = null
  ): Observable<EmployeeModel[]> {
    const nameFilter = f().contains('name', name).toString();
    const registrationFilter = f().contains('registration', name.toUpperCase()).toString();

    const combinedFilter = `(${nameFilter.toString()} or ${registrationFilter.toString()})`;

    const filter = [combinedFilter];

    if (companyId !== undefined && companyId !== null) {
      filter.unshift(Filter.buildFilter('companyId', companyId));
    }

    if (costCenterId !== undefined && costCenterId !== null && costCenterId.length > 0) {
      filter.unshift(Filter.inGuid('costCenterId', costCenterId));
    }

    if (timeShiftId !== undefined && timeShiftId !== null && timeShiftId.length > 0) {
      filter.unshift(Filter.inGuid('timeShiftId', timeShiftId));
    }

    if (jobtitleId !== undefined && jobtitleId !== null && jobtitleId.length > 0) {
      filter.unshift(Filter.inGuid('jobtitleId', jobtitleId));
    }

    return new Observable<any>(observer => {
      (this.list(
        {
          select: [
            'name',
            'cpf',
            'companyId',
            'costCenterId',
            'timeShiftId',
            'jobTitleId',
            'registration',
            'enable',
            'type',
            'workRegime',
          ],
          orderBy: 'name',
          orderByOrder: 'asc',
          filter: buildQuery({ filter }).replace('?', '&'),
        } as QueryParams,
        true
      ).subscribe(res => {
        observer.next(res.data.filter(e => e.enable));
      }),
        err => {
          observer.error(err);
        });
    });
  }

  changePermissions(employeeId: string, permissions: PermissionModel[]): Observable<any> {
    return this.http.put(`employee/permissions/${employeeId}`, permissions);
  }

  listEmployeePermissions(employeeId: string): Observable<Response<PermissionModel[]>> {
    return this.http.get(`employee/list-employee-permissions/${employeeId}`).pipe(
      map((res: any) => {
        const response = new Response<PermissionModel[]>();
        response.data = res.body.map((e: PermissionModel) => PermissionModel.create(e));
        return response;
      })
    );
  }

  changeTimeShift(transfer: TimeShiftHistoryModel) {
    return this.http.post(`${this.endpoint}/change.timeshift`, transfer, null, null, true);
  }

  uploadProfileImage(employeeId: string, model: FileModel) {
    return this.http.post(`${this.endpoint}/upload/profile-image/${employeeId}`, model);
  }

  getDependents(employeeId: string) {
    return this.http.get(`employee/${employeeId}/dependents/odata`, null, null, null, true).pipe(
      map((res: any) => {
        const response = new Response<ApplicantDependentModel[]>();

        response.data = res.body.map((e: ApplicantDependentModel) =>
          ApplicantDependentModel.create(e)
        );

        return response;
      })
    );
  }

  getByName(name: string): Observable<EmployeeModel[]> {
    const filter = Filter.contains('name', name);

    return new Observable<any>(observer => {
      (this.list(
        {
          select: [
            'id',
            'name',
            'profileImagePath',
            'email',
            'companyName',
            'companyId',
            'jobTitleId',
            'jobTitleName',
            'registration',
            'type',
            'workRegime',
            'shoeSize',
            'shirtSize',
            'pantsSize',
            'enable',
          ],
          orderBy: 'name',
          orderByOrder: 'asc',
          filter: buildQuery({ filter }).replace('?', '&'),
        } as QueryParams,
        true
      ).subscribe(res => {
        observer.next(res.data.filter(e => e.enable));
      }),
        err => {
          observer.error(err);
        });
    });
  }

  getByNameOrRegistration(searchTerm: string): Observable<EmployeeModel[]> {
    const searchWords = searchTerm
      .toLocaleLowerCase()
      .split(' ')
      .filter(w => w); // remove espaços em branco

    // cria filtros para cada palavra
    const nameFilters = searchWords.map(word => `contains(tolower(name), '${word}')`);
    const combinedNameFilter = nameFilters.join(' and '); // todas as palavras precisam estar no nome

    // filtro final, incluindo registration
    const combinedFilter = `(${combinedNameFilter} or contains(tolower(registration), '${searchTerm.toLowerCase()}'))`;

    return new Observable<EmployeeModel[]>(observer => {
      this.list(
        {
          select: [
            'id',
            'name',
            'profileImagePath',
            'email',
            'companyName',
            'companyId',
            'jobTitleId',
            'jobTitleName',
            'registration',
            'type',
            'workRegime',
            'shoeSize',
            'shirtSize',
            'pantsSize',
            'enable',
          ],
          orderBy: 'name',
          orderByOrder: 'asc',
          filter: buildQuery({ filter: combinedFilter }).replace('?', '&'),
        } as QueryParams,
        true
      ).subscribe({
        next: res => observer.next(res.data.filter(e => e.enable)),
        error: err => observer.error(err),
      });
    });
  }

  createAggregateDependent(data: any) {
    return this.http.post(`${this.endpoint}/dependents`, [data], null, null, true);
  }

  getBenefitDependents(employeeId: string): Observable<BenefitDependentModel[]> {
    return this.http.get(`employee/${employeeId}/dependents/odata`, null, null, null, true).pipe(
      map((res: any) => {
        return res.body.map(item => BenefitDependentModel.create(item));
      })
    );
  }

  getEmployeeDependentsForBenefits(
    employeeId: string,
    forDependents: boolean,
    forRelatives: boolean
  ): Observable<BenefitDependentModel[]> {
    let filter = '';

    if (forDependents && !forRelatives) {
      filter = Filter.notEqual('kinshipDegree', KinshipDegreeTypeEnum.Relative.value);
    } else if (!forDependents && forRelatives) {
      filter = Filter.equal('kinshipDegree', KinshipDegreeTypeEnum.Relative.value);
    }

    const params = {
      select: ['id', 'name', 'birthDate', 'rg', 'cpf', 'kinshipDegree', 'sex', 'employeeId'],
      orderBy: 'name',
      orderByOrder: 'asc',
      filter: filter,
    } as QueryParams;

    const query = this.createBuildQuery(params);

    return this.http
      .get(`employee/${employeeId}/dependents/odata${query}`, null, null, null, true)
      .pipe(
        map((res: any) => {
          return res.body.map(item => BenefitDependentModel.create(item));
        })
      );
  }

  updateContact(employee: EmployeeModel): Observable<any> {
    return this.http.put(`employee/update-contact`, employee);
  }

  updateUniform(employee: EmployeeModel): Observable<any> {
    return this.http.put(`employee/update-uniform`, employee);
  }

  getByField(field: string, value: string): Observable<EmployeeModel[]> {
    const filter = Filter.contains(field, value);

    return new Observable<any>(observer => {
      (this.list(
        {
          select: [
            'id',
            'name',
            'profileImagePath',
            'email',
            'companyName',
            'companyId',
            'jobTitleId',
            'jobTitleName',
            'registration',
            'type',
            'workRegime',
            'shoeSize',
            'shirtSize',
            'pantsSize',
            'enable',
          ],
          orderBy: 'name',
          orderByOrder: 'asc',
          filter: buildQuery({ filter }).replace('?', '&'),
        } as QueryParams,
        true
      ).subscribe(res => {
        observer.next(res.data.filter(e => e.enable));
      }),
        err => {
          observer.error(err);
        });
    });
  }

  public getByIds(ids: string[]): Observable<EmployeeModel[]> {
    if (!ids?.length) {
      return of([]);
    }

    const params = [
      `$select=id,name,enable,cpf,email`,
      `$filter=enable eq true and (${ids.map(id => `id eq ${id}`).join(' or ')})`,
      `$orderby=name asc`,
    ];

    const queryParams = `?${params.join('&')}`;

    return this.http
      .get(`${this.endpoint}/odata${queryParams}`, null, null, null, true)
      .pipe(map((res: any) => res.body || res));
  }

  getListFiltered(params: string[] = []): Observable<EmployeeModel[]> {
    const queryParams = `?${params.join('&')}`;

    return this.http
      .get(`${this.endpoint}/odata${queryParams}`, null, null, null, true)
      .pipe(map((res: any) => (res?.body || []).map((item: any) => EmployeeModel.create(item))));
  }

  getListFilteredWithCount(
    params: string[] = []
  ): Observable<{ data: EmployeeModel[]; count: number }> {
    const queryParams = `?${params.join('&')}`;

    return this.http.get(`${this.endpoint}/odata${queryParams}`, null, null, null, true).pipe(
      map((res: any) => {
        const countHeader = res?.headers?.get('x-count');
        const count = countHeader ? parseInt(countHeader, 10) : 0;
        return {
          data: (res?.body || []).map((item: any) => EmployeeModel.create(item)),
          count: count,
        };
      })
    );
  }

  public getByIdsBatched(ids: string[], batchSize: number = 50): Observable<EmployeeModel[]> {
    if (!ids?.length) {
      return of([]);
    }

    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += batchSize) {
      batches.push(ids.slice(i, i + batchSize));
    }

    const requests = batches.map(batch => {
      const params = [
        `$select=id,name,enable,cpf,email`,
        `$filter=enable eq true and (${batch.map(id => `id eq ${id}`).join(' or ')})`,
        `$orderby=name asc`,
      ];

      return this.http
        .get(`${this.endpoint}/odata?${params.join('&')}`, null, null, null, true)
        .pipe(map((res: any) => (res?.body || []).map((e: any) => EmployeeModel.create(e))));
    });

    return forkJoin(requests).pipe(
      map((responses: EmployeeModel[][]) =>
        responses.reduce((acc: EmployeeModel[], curr: EmployeeModel[]) => acc.concat(curr), [])
      )
    );
  }

  performResignation(employeeId: string, resignationDate: string): Observable<Response<any>> {
    return this.http.post(
      `${this.endpoint}/resign`,
      { employeeId, resignationDate },
      null,
      null,
      true
    );
  }

  searchEmployees(payload: any): Observable<Response<EmployeeModel[]>> {
    let params = new HttpParams();

    const appendArray = (key: string, values?: string[]) => {
      values?.forEach(v => {
        params = params.append(key, v);
      });
    };

    // arrays
    appendArray('companyIds', payload.companyIds);
    appendArray('costCenterIds', payload.costCenterIds);
    appendArray('jobTitleFamilyIds', payload.jobTitleFamilyIds);
    appendArray('jobTitleGroupIds', payload.jobTitleGroupIds);
    appendArray('jobTitleIds', payload.jobTitleIds);
    appendArray('timeShiftIds', payload.timeShiftIds);

    const queryParams = {
      select: ['id'],
    } as QueryParams;

    const query = this.createBuildQuery(queryParams);

    return this.http
      .get(`${this.endpoint}/search${query}`, { params }, this.baseUrl, null, true)
      .pipe(
        map(res => {
          const response = new Response<EmployeeModel[]>();
          response.data = res.body.map(pp => this.createEntity(pp) as EmployeeModel);
          response.count = response.data.length > 0 ? res.headers.get('x-count') : 0;
          return response;
        })
      );
  }

  private _buildNameOrRegistrationODataFilter(searchTerm: string): string {
    const normalizedTerm = searchTerm.toLocaleLowerCase();
    const searchWords = normalizedTerm.split(' ').filter(word => word);
    const nameFilters = searchWords.map(word => `contains(tolower(name), '${word}')`);
    const combinedNameFilter = nameFilters.join(' and ');
    const combinedFilter = `(${combinedNameFilter} or contains(tolower(registration), '${normalizedTerm}'))`;

    return combinedFilter;
  }
}
